import { useState, useEffect } from 'react'
import { message } from 'antd'
import { handleError } from '../../../../lib/utils'
import { readBillingRecords } from '../../../../Bookings/utils/utils'
import { readPatients } from '../../../../Admin/patients/utils/utils'
import { createPatientPayment } from '../utils'
import { useDispatch, useSelector } from 'react-redux'
import coreReducer from '../../../../../core/utils/reducer'
import CoreEnums from '../../../../../core/utils/enums'
import dayjs from 'dayjs'

/**
 * Hook for managing patient payment functionality
 * @param {Object} data - Initial data for the patient payment
 * @returns {Object} Functions and state for patient payment
 */
export const usePatientPayment = (data) => {
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [paymentData, setPaymentData] = useState(data)
  const [items, setItems] = useState([])
  const [renderForm, setRenderForm] = useState(false)
  const [patientLiableTotal, setPatientLiableTotal] = useState(0)

  const agiliteUser = useSelector((state) => state.auth.agiliteUser)

  /**
   * Gets billing records for the current booking
   */
  const handleGetBillingRecords = async () => {
    let tmpItems = []

    setLoading(true)
    try {
      tmpItems = await readBillingRecords({ bookingRef: data.bookingRef })

      for (const entry of tmpItems) {
        entry.total = entry.total ? entry.total : '0'
        entry.total = entry.total ? entry.total : '0'
        entry.patient = entry.patient ? entry.patient : '0'
        entry.payment = entry.payment ? entry.payment : '0'
        entry.discount = entry.discount ? entry.discount : '0'
        entry.comments = entry.comments ? entry.comments : ''

        entry.patientBalance = entry.patient
        entry.oldPatient = entry.patient
        entry.oldMedicalAid = entry.medicalAid
      }

      setItems(tmpItems)
    } catch (e) {
      message.error(handleError(e, true))
    }
    setLoading(false)
  }

  /**
   * Gets extended data such as patient information
   */
  const handleGetExtendedData = async () => {
    let tmpData = null
    let patientRecord = null

    setFormLoading(true)
    try {
      await Promise.all([
        (async () => {
          tmpData = await readPatients({ _id: data.userRef }, {}, 'firstName lastName idNo medicalAid')
          patientRecord = tmpData.length > 0 ? tmpData[0] : null
        })()
      ])

      setPaymentData({
        ...data,
        patientRecord
      })
      setRenderForm(true)
    } catch (e) {
      message.error(handleError(e, true))
    }
    setFormLoading(false)
  }

  /**
   * Allocates payment based on form data
   * @param {Object} form - Form instance containing payment data
   */
  const handleAllocatePayment = async (form) => {
    try {
      await form.validateFields()

      setSubmitLoading(true)
      try {
        await createPatientPayment({
          createdAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
          createdBy: agiliteUser._id,
          invoiceRef: data._id,
          userRef: data.userRef,
          userSchemeCode: paymentData.patientRecord.medicalAid.schemeCode,
          clinicRef: data.clinicRef,
          medicalProfRef: data.medicalProfRef,
          receiptNumber: form.getFieldValue('practiceReceiptNumber'),
          receiptDate: form.getFieldValue('receiptDate'),
          accStatus: form.getFieldValue('accountStatus'),
          paymentMethod: form.getFieldValue('paymentMethod'),
          paymentAmount: form.getFieldValue('paymentAmount'),
          items
        })
        message.success('Payment successfully allocated')
        handleCloseTab()
      } catch (error) {
        message.error(handleError(error, true))
      }
      setSubmitLoading(false)
    } catch (e) {
      // Form validation error
    }
  }

  /**
   * Closes the current tab
   */
  const handleCloseTab = () => {
    dispatch(
      coreReducer.actions.closeTab({
        targetKey: `${CoreEnums.tabKeys.PATIENT_PAYMENTS}_new`,
        removeBreadcrumb: true
      })
    )
  }

  /**
   * Handles changes to form fields
   * @param {Object} changedFields - Fields that have changed
   */
  const handleFieldChange = (changedFields) => {
    try {
      // Only reset payment values when paymentAmount is explicitly cleared
      if (changedFields.paymentAmount === undefined || changedFields.paymentAmount === '') {
        for (const entry of items) {
          entry.payment = 0
          // Reset patient and patientBalance to their original values when payment amount is cleared
          entry.patient = entry.oldPatient
          entry.patientBalance = entry.oldPatient
        }
      }

      if (parseFloat(changedFields.paymentAmount) === patientLiableTotal) {
        // If the payment amount is equal to the total amount the patient is liable for
        // then set all payment values to the patient liable amounts
        for (const entry of items) {
          entry.payment = entry.patient
          entry.patientBalance = 0
          entry.patient = 0 // Also update the patient property to ensure the total in the footer updates correctly
        }
      }

      setPaymentData({ ...paymentData, ...changedFields })
      setItems([...items])
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  /**
   * Handles changes to table field values
   * @param {string} prop - Property to change
   * @param {number} index - Index of the item in the items array
   * @param {any} value - New value
   */
  const handleTableFieldChange = (prop, index, value) => {
    const tmpItems = JSON.parse(JSON.stringify(items))
    tmpItems[index][prop] = value

    try {
      // When the payment amount on a row is changed
      if (prop === 'payment') {
        const tmpPayment = value
        const originalPatientAmount = tmpItems[index].oldPatient // Use the original patient amount for validation

        if (tmpPayment > parseFloat(originalPatientAmount)) {
          throw new Error('Payment amount cannot be greater than the Patient Liable amount')
        } else {
          tmpItems[index].patientBalance = (parseFloat(originalPatientAmount) - tmpPayment).toFixed(2)
          // Also update the patient property to ensure the total in the footer updates correctly
          tmpItems[index].patient = (parseFloat(originalPatientAmount) - tmpPayment).toFixed(2)
        }
      }

      // When the discount amount on a row is changed
      if (prop === 'discount') {
        const tmpDiscount = value
        const originalPatientAmount = tmpItems[index].oldPatient // Use the original patient amount for validation

        if (tmpDiscount > parseFloat(originalPatientAmount)) {
          throw new Error('Discount amount cannot be greater than the Patient Liable amount')
        } else {
          tmpItems[index].patientBalance = (parseFloat(originalPatientAmount) - tmpDiscount).toFixed(2)
          // Also update the patient property to ensure the total in the footer updates correctly
          tmpItems[index].patient = (parseFloat(originalPatientAmount) - tmpDiscount).toFixed(2)
        }
      }

      setItems(tmpItems)
    } catch (e) {
      message.error(e.message)
    }
  }

  /**
   * Generates totals data for the table footer
   * @returns {Array} Array containing totals data
   */
  const generateTotalsData = () => {
    const data = [
      {
        total: items.reduce((a, b) => a + parseFloat(b.total), 0).toFixed(2),
        medicalAid: items.reduce((a, b) => a + parseFloat(b.medicalAid), 0).toFixed(2),
        patient: items.reduce((a, b) => a + parseFloat(b.patient), 0).toFixed(2),
        payment: items.reduce((a, b) => a + parseFloat(b.payment), 0).toFixed(2),
        discount: items.reduce((a, b) => a + parseFloat(b.discount), 0).toFixed(2)
      }
    ]

    for (const entry of data) {
      for (const prop in entry) {
        if (isNaN(entry[prop])) {
          entry[prop] = 0
        }
      }
    }

    return data
  }

  // Calculate patientLiableTotal when items change
  useEffect(() => {
    let tmpPatientLiableTotal = 0

    for (const entry of items) {
      tmpPatientLiableTotal += parseFloat(entry.patient)
    }

    setPatientLiableTotal(tmpPatientLiableTotal)
  }, [items])

  // Load data on mount
  useEffect(() => {
    handleGetBillingRecords()
    handleGetExtendedData()
    // eslint-disable-next-line
  }, [])

  return {
    loading,
    formLoading,
    submitLoading,
    paymentData,
    items,
    renderForm,
    patientLiableTotal,
    handleGetBillingRecords,
    handleGetExtendedData,
    handleAllocatePayment,
    handleCloseTab,
    handleFieldChange,
    handleTableFieldChange,
    generateTotalsData,
    setItems
  }
}
